<template>
  <div>
    <!-- Menu Overlay -->
    <div
      ref="menuOverlay"
      class="fixed inset-0 bg-black z-40 opacity-0 invisible"
      :class="{ 'menu-active': isMenuOpen }"
    >
      <!-- Header do Menu -->
      <header class="fixed top-0 left-0 right-0 z-50 bg-transparent">
        <div class="max-w-7xlflex items-center h-full relative w-full px-2 lg:px-4">
          <div class="flex justify-between items-center h-20">
            <!-- Logo FAZ -->
            <div class="flex items-center">
              <router-link :to="getLocalizedRoute('')" @click="closeMenu">
                <img 
                  src="/assets/images/logo-faz.svg" 
                  alt="FAZ" 
                  :style="{ 
                    width: logoWidth + 'px', 
                    height: 'auto',
                    filter: 'brightness(0) invert(1)'
                  }"
                >
              </router-link>
            </div>
            
            <!-- Texto Central -->
            <div class="absolute left-1/2 transform -translate-x-1/2">
              <span class="text-md font-hanken text-white tracking-wider whitespace-nowrap">{{ $t('common.acontecer') }}</span>
            </div>

            <!-- Botão Fechar (X) -->
            <div class="flex items-center justify-center w-10 h-20">
              <button
                ref="closeButton"
                @click="closeMenu"
                class="relative w-8 h-8 flex items-center justify-center transition-colors duration-300 text-white"
              >
                <span class="close-line w-8 h-0.5 bg-current transition-all duration-300 absolute"></span>
                <span class="close-line w-8 h-0.5 bg-current transition-all duration-300 absolute"></span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <!-- Conteúdo Principal -->
      <div class="flex flex-col justify-center items-start h-full px-6 lg:px-8 max-w-7xl mx-auto">
        <div class="main-content opacity-0">
          <h1 class="text-white text-5xl md:text-6xl lg:text-7xl font-light font-hanken mb-6 lg:mb-8">
            <router-link :to="getLocalizedRoute('about')" @click="closeMenu" class="main-link-mobile">
              {{ $t('common.quemSomos') }}
            </router-link>
          </h1>
          <h2 class="text-white text-5xl md:text-6xl lg:text-7xl font-light font-hanken">
            <router-link :to="getLocalizedRoute('contact')" @click="closeMenu" class="main-link-mobile">
              {{ $t('common.falamos') }}
            </router-link>
          </h2>
        </div>
      </div>

      <!-- Footer -->
      <footer class="fixed bottom-0 left-0 right-0 z-50 bg-transparent">
        <div class="max-w-7xl mx-auto px-6 lg:px-8 py-6 sm:py-8">
          <div class="flex flex-col sm:flex-row sm:justify-between sm:items-end space-y-4 sm:space-y-0">
            <!-- Links -->
            <div class="flex flex-wrap gap-4 sm:gap-8 social-links opacity-0">
              <!-- Work With Us oculto
              <router-link :to="getLocalizedRoute('work-with-us')" @click="closeMenu" class="footer-link-mobile text-white">
                <span class="font-hanken text-xs sm:text-sm tracking-wider whitespace-nowrap">{{ $t('common.vagas') }}</span>
              </router-link>
              -->
              <a href="mailto:hello@wearefaz.com" class="footer-link-mobile text-white">
                <span class="font-hanken text-xs sm:text-sm tracking-wider whitespace-nowrap">HELLO@WEAREFAZ.COM</span>
              </a>
              <a href="https://www.instagram.com/faz.acontecer__/?igsh=MTM2ZWlqOXB2OTA2YQ%3D%3D#" target="_blank" class="footer-link-mobile text-white">
                <span class="font-hanken text-xs sm:text-sm tracking-wider whitespace-nowrap">{{ $t('common.instagram') }}</span>
              </a>
              <a v-if="false" href="#" class="footer-link-mobile text-white">
                <span class="font-hanken text-xs sm:text-sm tracking-wider whitespace-nowrap">{{ $t('common.linkedin') }}</span>
              </a>
            </div>
            
            <!-- Copyright -->
            <div class="copyright opacity-0">
              <span class="text-white font-hanken text-xs sm:text-sm tracking-wider whitespace-nowrap">{{ $t('common.copyright') }}</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  </div>
</template>

<script>
import { gsap } from 'gsap'
import { useI18n } from 'vue-i18n'

export default {
  name: 'MobileMenu',
  props: {
    isMenuOpen: {
      type: Boolean,
      default: false
    },
    logoWidth: {
      type: Number,
      default: 116
    }
  },
  emits: ['close'],
  setup() {
    const { locale } = useI18n()
    
    // Função para gerar rotas localizadas
    const getLocalizedRoute = (routeName) => {
      const currentLocale = locale.value === 'en-US' ? 'en' : 'pt'
      return routeName ? `/${currentLocale}/${routeName}` : `/${currentLocale}`
    }

    return {
      getLocalizedRoute
    }
  },
  mounted() {
    // Inicializar posições dos elementos
    gsap.set('.main-content', { opacity: 0, y: 30 })
    gsap.set('.social-links', { opacity: 0 })
    gsap.set('.copyright', { opacity: 0 })
  },
  watch: {
    isMenuOpen(newValue) {
      if (newValue) {
        this.openMenu()
      } else {
        this.closeMenu()
      }
    }
  },
  methods: {
    openMenu() {
      document.body.style.overflow = 'hidden'
      
      // Animação de abertura do menu
      const tl = gsap.timeline()
      
      tl.to(this.$refs.menuOverlay, {
        opacity: 1,
        visibility: 'visible',
        duration: 0.3
      })
      .to('.main-content', {
        opacity: 1,
        y: 0,
        duration: 0.6,
        ease: 'power2.out'
      }, '-=0.2')
      .to('.social-links', {
        opacity: 1,
        duration: 0.4
      }, '-=0.3')
      .to('.copyright', {
        opacity: 1,
        duration: 0.4
      }, '-=0.3')
    },
    
    closeMenu() {
      const tl = gsap.timeline({
        onComplete: () => {
          document.body.style.overflow = 'auto'
          this.$emit('close')
        }
      })
      
      tl.to('.copyright', {
        opacity: 0,
        duration: 0.2
      })
      .to('.social-links', {
        opacity: 0,
        duration: 0.2
      }, '-=0.1')
      .to('.main-content', {
        opacity: 0,
        y: -20,
        duration: 0.3,
        ease: 'power2.in'
      }, '-=0.1')
      .to(this.$refs.menuOverlay, {
        opacity: 0,
        visibility: 'hidden',
        duration: 0.3
      }, '-=0.2')
    }
  }
}
</script>

<style scoped>
/* Smooth transitions */
.main-content {
  will-change: opacity, transform;
}

.social-links {
  will-change: opacity;
}

.copyright {
  will-change: opacity;
}

/* Footer Links com Underline Animado - Mobile */
.footer-link-mobile {
  position: relative;
  text-decoration: none;
}

.footer-link-mobile::before {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 0;
  height: 1px;
  background-color: currentColor;
  transition: width 0.3s ease;
}

.footer-link-mobile:hover::before {
  width: 100%;
}

/* Main Links com Underline Animado - Mobile */
.main-link-mobile {
  position: relative;
  text-decoration: none;
  color: white;
  display: inline-block;
}

.main-link-mobile::before {
  content: '';
  position: absolute;
  bottom: -4px;
  left: 0;
  width: 0;
  height: 2px;
  background-color: currentColor;
  transition: width 0.3s ease;
}

.main-link-mobile:hover::before {
  width: 100%;
}

/* Animação do botão X */
.close-line {
  transform-origin: center;
  position: absolute;
  top: 50%;
  left: 50%;
}

.close-line:nth-child(1) {
  transform: translate(-50%, -50%) rotate(45deg);
}

.close-line:nth-child(2) {
  transform: translate(-50%, -50%) rotate(-45deg);
}

/* Responsividade adicional para o menu mobile */
@media (max-width: 480px) {
  .main-content h1,
  .main-content h2 {
    line-height: 1.1;
  }
  
  .footer-link-mobile {
    padding: 2px 0;
  }
}
</style> 